import { TextTemplateComponent } from '../../test/text-template/text-template.component';
import { Destination } from '../models/destination.model';
import { FLASH_CALL_CHANNEL } from '../models/flash-call.model';
import { TestStatusEnum } from '../models/test-group.model';
import { SmscInfoDto, TestResult, TestResultAction } from '../models/test-result.model';
import { BrowserUtils } from './browser-utils';
import { CustomUtils } from './custom-utils';
declare const moment: any;

export class TestResultUtils {
  static prepareTestResult(result: TestResult) {
    result.textSentBackend = this.prepareTextSentBackend(result.textSent, result.telqId, result.senderSent);
    return result;
  }

  static prepareTextSentBackend(textSent: string, telqId: string, senderSent: string): string {
    if (!textSent) {
      return null;
    }
    let textSentBackend = textSent;
    textSentBackend = textSentBackend.replaceAll(TextTemplateComponent.PLACEHOLDER_MARKER, telqId);
    textSentBackend = textSentBackend.replaceAll('@sender', senderSent);
    if (textSentBackend.indexOf(telqId) === -1) {
      textSentBackend = telqId + ' ' + textSentBackend;
    }

    return textSentBackend;
  }

  // html render utils
  static renderDestination(dest: Destination, icon = false): string {
    if (dest.mnc === null && dest.mcc === null) {
      return (dest.countryName ? dest.countryName : 'Unknown') + '/' + dest.phone;
    }

    let str =
      (dest.countryName ? dest.countryName : 'Unknown') +
      '(' +
      dest.mcc +
      ')/' +
      (dest.providerName ? dest.providerName : 'Unknown') +
      '(' +
      dest.mnc +
      ')';

    if (dest.originalMnc) {
      const iconStr = icon ? '<i class="icon-ported"></i>' : '<=';
      const provider = dest.originalProviderName ? dest.originalProviderName : 'Unknown';
      let originalStr = ' ' + iconStr + ' ' + provider + '(' + dest.originalMnc + ')';
      if (icon) {
        originalStr = `<span title="Ported from ${provider}">${originalStr}</span>`;
      }
      str += originalStr;
    }

    return str;
  }

  static renderMntTextDelivered(textDelivered: string, className = '') {
    if (!textDelivered) {
      return BrowserUtils.escapeHtml(textDelivered);
    }
    const text = BrowserUtils.escapeHtml(String(textDelivered));
    return `<div class="one-line ${className}"><span>${text}</span></div>`;
  }

  static renderMntPhoneSmscInfo(
    destinationDto: Destination,
    smscInfoDto: Pick<SmscInfoDto, 'countryName' | 'countryCode' | 'phone'>,
    wrapperClassName = ''
  ) {
    const result = [];
    if (destinationDto.phone) {
      result.push(`<span>${destinationDto.phone}</span>`);
    }
    const hlr = smscInfoDto;
    const onlyNumber = hlr.phone && !hlr.countryName;
    if (hlr !== null && ((hlr.countryName && hlr.countryCode) || onlyNumber)) {
      const className = hlr.countryName !== destinationDto.countryName || onlyNumber ? 'text-error-r3' : 'text-success-s2';
      const title = onlyNumber ? hlr.phone : `${hlr.countryName} (${hlr.phone})`;
      const content = onlyNumber ? '?' : hlr.countryCode;
      result.push(`<span class="${className}" title="${title})">${content}</span>`);
    }
    return `<div class="two-lines ${wrapperClassName}">` + result.join('<span class="delimiter">/</span>') + '</div>';
  }

  static renderLntPhoneSmscInfo(
    destinationDto: Destination,
    smscInfoDto: Pick<SmscInfoDto, 'countryName' | 'countryCode' | 'phone'>,
    wrapperClassName = ''
  ) {
    const result = [];
    if (destinationDto.phone) {
      result.push(`<span>${destinationDto.phone}</span>`);
    }
    const hlr = smscInfoDto;
    if (hlr !== null) {
      const onlyNumber = hlr.phone && (!hlr.countryName || !hlr.countryCode);
      if ((hlr.countryName && hlr.countryCode) || onlyNumber) {
        const className = hlr.countryName !== destinationDto.countryName || onlyNumber ? 'text-error-r3' : 'text-success-s2';
        const title = onlyNumber ? hlr.phone : `${hlr.countryName} (${hlr.phone})`;
        const content = onlyNumber ? '?' : hlr.countryCode;
        result.push(`<span class="${className}" title="${title}">${content}</span>`);
      }
    }
    return `<div class="two-lines ${wrapperClassName}">` + result.join('<span class="delimiter">/</span>') + '</div>';
  }

  static renderLntText(textDeliveredFull: string, textSentBackend: string, wrapperClassName = '') {
    let className = '';
    if (textDeliveredFull) {
      className = textDeliveredFull !== textSentBackend ? 'text-error-r3' : 'text-success-s2';
    }
    const text = BrowserUtils.escapeHtml(textDeliveredFull ? textDeliveredFull : textSentBackend);
    return `<div class="one-line ${wrapperClassName}"><span class="${className}">${text}</span></div>`;
  }

  static renderDate(testResult: TestResultAction): string {
    const createdAtFormatted =
      typeof testResult.createdAt === 'string' ? moment(testResult.createdAt).format('DD/MM/YY HH:mm:ss').split(' ') : testResult.createdAt;

    return `<div class="two-lines"><span title="${testResult.taskId ? 'Scheduled' : ''}" class="${testResult.taskId ? 'text-primary' : ''}">${createdAtFormatted[1]} <br>${createdAtFormatted[0]}</span></div>`;
  }

  static renderDelay(receiptDelay?: number, smppDelay?: number) {
    const text = [];
    if (typeof smppDelay === 'number') {
      const delay = CustomUtils.secToHumanTime(smppDelay);
      text.push(smppDelay >= 300 ? `<span class="text-accent-a2">${delay}</span>` : delay);
    }
    if (typeof receiptDelay === 'number') {
      const delay = CustomUtils.secToHumanTime(receiptDelay);
      text.push(receiptDelay >= 300 ? `<span class="text-accent-a2">${delay}</span>` : delay);
    }
    return `<span>${text.join('/')}</span>`;
  }

  static renderSenderDelivered(senderDelivered: string, senderSent: string, wrapperClass?: string) {
    let className = '';
    if (senderDelivered) {
      className = senderDelivered !== senderSent ? 'text-error-r3' : 'text-success-s2';
    }
    const text = BrowserUtils.escapeHtml(senderDelivered ? senderDelivered : senderSent);
    return `<div class="two-lines"><span class="${className} ${wrapperClass}">${text}</span></div>`;
  }

  static isFlashCallDelivered(result: Pick<TestResult, 'testStatus' | 'channel'>): boolean {
    if (!result) {
      return false;
    }
    return result.testStatus === TestStatusEnum.FLASH_CALL_DETECTED || (result.channel || '').toLowerCase() === FLASH_CALL_CHANNEL;
  }

  // Matching ignores formatting (spaces, '+', dashes) like the backend does, so the highlight is mapped back onto the original characters.
  static highlightTestIdInCallerId(callerId: string, testId: string): string {
    if (!callerId) {
      return '';
    }
    const digitPositions: number[] = [];
    let digits = '';
    for (let i = 0; i < callerId.length; i++) {
      if (callerId[i] >= '0' && callerId[i] <= '9') {
        digits += callerId[i];
        digitPositions.push(i);
      }
    }
    const id = testId == null ? '' : String(testId);
    const matchIndex = id ? digits.indexOf(id) : -1;
    if (matchIndex === -1) {
      return BrowserUtils.escapeHtml(callerId);
    }
    const start = digitPositions[matchIndex];
    const end = digitPositions[matchIndex + id.length - 1] + 1;
    return (
      BrowserUtils.escapeHtml(callerId.slice(0, start)) +
      `<span class="flash-call-test-id" title="Test ID Text">${BrowserUtils.escapeHtml(callerId.slice(start, end))}</span>` +
      BrowserUtils.escapeHtml(callerId.slice(end))
    );
  }

  static renderFlashCallCallerId(callerId: string): string {
    return `<div class="two-lines"><span class="flash-call-caller-id" title="Delivered as flash call"><span class="icon-flash-call-phone"></span>${BrowserUtils.escapeHtml(callerId)}</span></div>`;
  }

  static renderFlashCallText(): string {
    return '<div class="one-line"><span class="text-neutral-n6">&mdash;</span></div>';
  }

  static renderConnection(row: TestResultAction, html = true) {
    const className = '';
    return html
      ? `<span class="${className}">${BrowserUtils.escapeHtml(row.supplierDto.title)} ${BrowserUtils.escapeHtml(row.supplierDto.routeType)}</span>`
      : BrowserUtils.escapeHtml(row.supplierDto.title + ' ' + row.supplierDto.routeType);
  }
}
